import React, { useState } from 'react';
import { useAtomValue, useAtom } from 'jotai';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Stack,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { gearIdAtom } from 'atoms/gearAtom';
import { GEAR_IDS } from 'utils/constsHev';

import { ProjectSelect } from "utils/projectSelectorLtool/ProjectSelect";
import { DestinationSelect } from "utils/projectSelectorLtool/DestinationSelect";
import { LotSelect } from "utils/projectSelectorLtool/LotSelect";
import { PhaseSelect } from "utils/projectSelectorLtool/PhaseSelect";
import { CalculationSelect } from "utils/projectSelectorLtool/CalculationSelect";
import {
    calculationIdAtom,
    destinationIdAtom,
    lotIdAtom,
    phaseIdAtom,
    projectIdAtom,
    copyToConfigAtom,
} from 'atoms/Ltool/copyToConfigAtoms';

import { SpdmImport } from './subcomponents/SpdmImport';
import { ProjectCopy } from './subcomponents/ProjectCopy';
import { ExcelImport } from './subcomponents/ExcelImport';
import { ManualDimensions } from './subcomponents/ManualDimensions';

export const DataRegistration: React.FC = () => {
    const config = useAtomValue(copyToConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const [gearId, setGearId] = useAtom(gearIdAtom);
    const [tabIndex, setTabIndex] = useState(0);

    const selectedPath = [
        config.project_id,
        config.destination_id,
        config.lot_id,
        config.phase_id,
        config.calculation_id
    ].filter(id => id).join(' / ');

    const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
        setTabIndex(newIndex);
    };

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>
                L 検討ツール データ登録
            </Typography>

            {selectedPath ? (
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    選択中のデータ登録先の計算セット：{selectedPath}
                </Typography>
            ) : (
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'error.main' }}>
                    データ登録先の計算セットが選択されていません。
                </Typography>
            )}

            {/* 登録先（ターゲット） */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    mb: 3,
                    borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
                    bgcolor: theme => theme.palette.action.hover
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Chip size="small" color="primary" label="登録先" />
                    <Typography variant="subtitle2" color="text.secondary">
                        この計算セットにデータを登録します。
                    </Typography>
                </Stack>

                <Stack
                    component="form"
                    spacing={2}
                    noValidate
                    autoComplete="off"
                    direction="column"
                    alignItems="stretch"
                >
                    <ProjectSelect atom={projectIdAtom} />
                    <DestinationSelect atom={destinationIdAtom} config={config} />
                    <LotSelect atom={lotIdAtom} config={config} />
                    <PhaseSelect atom={phaseIdAtom} config={config} />
                    <CalculationSelect atom={calculationIdAtom} config={config} />
                    {/* HEV は calculation 配下（unit の下）に gear を選ぶ（BEV では非表示） */}
                    {powertrain === 'hev' && (
                        <FormControl fullWidth>
                            <InputLabel id="gear-select-label">Gear</InputLabel>
                            <Select
                                labelId="gear-select-label"
                                value={gearId ?? ''}
                                label="Gear"
                                onChange={e => setGearId(e.target.value as string)}
                            >
                                {GEAR_IDS.map(g => (
                                    <MenuItem key={g} value={g}>{g}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Stack>
            </Paper>

            {/* 登録元（ソース） */}
            <Paper
                variant="outlined"
                sx={{
                    mb: 2,
                    borderLeft: theme => `4px solid ${theme.palette.secondary.main}`,
                    bgcolor: theme => theme.palette.action.selected
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1 }}>
                    <Chip size="small" color="secondary" label="登録元" />
                    <Typography variant="subtitle2" color="text.secondary">
                        取り込み方法を選択
                    </Typography>
                </Stack>

                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="secondary"
                    textColor="secondary"
                    variant="fullWidth"
                    sx={{ mt: 1 }}
                    aria-label="データ登録元の選択タブ"
                >
                    <Tab label="SPDM読み込み" />
                    <Tab label="他計算セットコピー" />
                    <Tab label="Excel読み込み" />
                    <Tab label="UI入力" />
                </Tabs>
            </Paper>

            {tabIndex === 0 && <SpdmImport />}
            {tabIndex === 1 && <ProjectCopy />}
            {tabIndex === 2 && <ExcelImport />}
            {tabIndex === 3 && <ManualDimensions />}

        </Box>
    );
};
